import React, { useEffect, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { formatCurrency, formatNumberWithDecimal, getCurrentFinancialYear } from "../../../utils/commonUtil";
import { REIButton } from "../../../components/ui";
import { Row } from "primereact/row";
import { ColumnGroup } from "primereact/columngroup";
import { ITrackerEquity } from "../../../interface/equity";
import { useNavigate } from "react-router-dom";
import { useFilterSelection } from "../FilterSelectionContext";

interface IEquityDetails {
  street: string;
  suburb: string;
  state: string;
  postcode: string;
  country: string;
  type: string;
  purchasePrice: number;
  purchaseDate: Date;
  currentMarketEstimation: number;
  currentMarketEstimationDate: Date;
  loanValue: number;
  capitalGrowth: number;
  netEquity: number;
}

interface IEquitySummary {
  type: string;
  purchasePrice: number;
  purchaseDate: Date;
  currentMarketEstimation: number;
  currentMarketEstimationDate: Date;
  loanValue: number;
  capitalGrowth: number;
  netEquity: number;
}

const EquityDetail: React.FC<{
  equityData: ITrackerEquity;
  isForecastOnly: boolean;
  year: string;
  isFooterVisible: boolean;
  isFromMyPropertyShowingTotals?: boolean;
  isMyPropertyParent?: boolean;
}> = ({
  equityData,
  isForecastOnly,
  year,
  isFooterVisible,
  isFromMyPropertyShowingTotals,
  isMyPropertyParent = false,
}) => {
  const [transformedData, setTransformedData] = useState<IEquityDetails[]>([]);
  const [transformedFooter, setTransformedFooter] = useState([]);
  const [collapsedRows, setCollapsedRows] = useState<Record<string, boolean>>({});
  const currentYear = "20" + getCurrentFinancialYear();
  const { isShowingTotals } = useFilterSelection();
  const [isShowingTotal, setIsShowingTotal] = useState(isShowingTotals);
  const navigate = useNavigate();

  useEffect(() => {
    if (isMyPropertyParent) {
      setIsShowingTotal(isFromMyPropertyShowingTotals !== undefined ? isFromMyPropertyShowingTotals : true);
    }
  }, [isFromMyPropertyShowingTotals]);

  useEffect(() => {
    if (!isMyPropertyParent) {
      setIsShowingTotal(isShowingTotals);
    }
  }, [isShowingTotals]);

  useEffect(() => {
    if (equityData && Array.isArray(equityData.PropertyDetails) && equityData.PropertyDetails.length > 0) {
      let groupCounter: number = -1;
      let lastGroupKey: string = "";
      const transformed = equityData.PropertyDetails.map((equities: any) => {
        const groupKey = equities.EquityPropertyDetail.StreetAddress;
        if (groupKey !== lastGroupKey) {
          groupCounter++;
          lastGroupKey = groupKey;
        }
        const actual = !isShowingTotal
          ? equities.EquityInDetailsPortions?.Actuals?.find(
              (item: any) => item.Year === year && Number(year) <= Number(currentYear)
            ) || {}
          : equities.EquityInDetailsTotals?.Actuals?.find(
              (item: any) => item.Year === year && Number(year) <= Number(currentYear)
            ) || {};
        const forecast = !isShowingTotal
          ? equities.EquityInDetailsPortions?.Forecast?.find((item: any) => item.Year === year) || {}
          : equities.EquityInDetailsTotals?.Forecast?.find((item: any) => item.Year === year) || {};
        const variance = !isShowingTotal
          ? equities.EquityInDetailsPortions?.Variance?.find(
              (item: any) => item.Year === year && Number(year) <= Number(currentYear)
            ) || {}
          : equities.EquityInDetailsTotals?.Variance?.find(
              (item: any) => item.Year === year && Number(year) <= Number(currentYear)
            ) || {};

        return [
          { type: "Forecast", data: forecast },
          { type: "Actuals", data: actual },
          { type: "Variance", data: variance },
        ].map(({ type, data }) => ({
          street: equities.EquityPropertyDetail?.StreetAddress || "",
          suburb: equities.EquityPropertyDetail?.Suburb || "",
          state: equities.EquityPropertyDetail?.State || "",
          postcode: equities.EquityPropertyDetail?.Postcode || "",
          country: equities.EquityPropertyDetail?.Country || "",
          type,
          purchasePrice: Number(data.PurchasePrice) || 0,
          purchaseDate: data.PurchaseDate,
          currentMarketEstimation: Number(data.CurrentValuation) || 0,
          currentMarketEstimationDate: data.ValuationDate || "",
          loanValue: Number(data.MortgageBalance) || 0,
          capitalGrowth: Number(data.AverageGrowth) || 0,
          netEquity: Number(data.EquityInProperty) || 0,
          propertyId: equities.EquityPropertyDetail.PortfolioTrackerId,
          groupIndex: groupCounter % 2,
        }));
      }).flat();

      setTransformedData(transformed);
      isFooterVisible && setFooterData();
    }
  }, [equityData, year, isShowingTotal]);

  const setFooterData = () => {
    const footerProperties = equityData.TotalForDetails.find((item: any) => item.EntityId === "") || {
      EquityTotalsInDetailsTotals: { Actuals: [], Forecast: [], Variance: [] },
      EquityTotalsInDetailsPortions: { Actuals: [], Forecast: [], Variance: [] },
    };
    const actualFooter = isShowingTotal
      ? footerProperties.EquityTotalsInDetailsTotals?.Actuals?.find((item: any) => item.Year === year)
      : footerProperties.EquityTotalsInDetailsPortions?.Actuals?.find((item: any) => item.Year === year) || {
          PurchasePrice: 0,
          CurrentValuation: 0,
          MortgageBalance: 0,
          AverageGrowth: 0,
          EquityInProperty: 0,
        };
    const forecastFooter = isShowingTotal
      ? footerProperties.EquityTotalsInDetailsTotals?.Forecast?.find((item: any) => item.Year === year)
      : footerProperties.EquityTotalsInDetailsPortions?.Forecast?.find((item: any) => item.Year === year) || {
          PurchasePrice: 0,
          CurrentValuation: 0,
          MortgageBalance: 0,
          AverageGrowth: 0,
          EquityInProperty: 0,
        };
    const varianceFooter = isShowingTotal
      ? footerProperties.EquityTotalsInDetailsTotals?.Variance?.find((item: any) => item.Year === year)
      : footerProperties.EquityTotalsInDetailsPortions?.Variance?.find((item: any) => item.Year === year) || {
          PurchasePrice: 0,
          CurrentValuation: 0,
          MortgageBalance: 0,
          AverageGrowth: 0,
          EquityInProperty: 0,
        };

    const footer = [
      {
        type: "Actuals",
        purchasePrice: Number(actualFooter?.PurchasePrice) || 0,
        purchaseDate: "",
        currentMarketEstimation: Number(actualFooter?.CurrentValuation) || 0,
        currentMarketEstimationDate: "",
        loanValue: Number(actualFooter?.MortgageBalance) || 0,
        capitalGrowth: Number(actualFooter?.AverageGrowth) || 0,
        netEquity: Number(actualFooter?.EquityInProperty) || 0,
      },
      {
        type: "Forecast",
        purchasePrice: Number(forecastFooter?.PurchasePrice) || 0,
        purchaseDate: "",
        currentMarketEstimation: Number(forecastFooter?.CurrentValuation) || 0,
        currentMarketEstimationDate: "",
        loanValue: Number(forecastFooter?.MortgageBalance) || 0,
        capitalGrowth: Number(forecastFooter?.AverageGrowth) || 0,
        netEquity: Number(forecastFooter?.EquityInProperty) || 0,
      },
      {
        type: "Variance",
        purchasePrice: Number(varianceFooter?.PurchasePrice) || 0,
        purchaseDate: "",
        currentMarketEstimation: Number(varianceFooter?.CurrentValuation) || 0,
        currentMarketEstimationDate: "",
        loanValue: Number(varianceFooter?.MortgageBalance) || 0,
        capitalGrowth: Number(varianceFooter?.AverageGrowth) || 0,
        netEquity: Number(varianceFooter?.EquityInProperty) || 0,
      },
    ];

    setTransformedFooter(footer as any);
  };

  const currencyTemplate = (value: number) => (
    <span className={value < 0 ? "text-red-600" : ""}>
      {(value == null || value === undefined || value === 0) && Number(year) > Number(currentYear)
        ? ""
        : value < 0
          ? `(${formatCurrency(-value)})`
          : formatCurrency(value)}
    </span>
  );

  const percentageTemplate = (value: number) => (
    <span className={value < 0 ? "text-red-600" : ""}>
      {(value == null || value === undefined || value === 0) && Number(year) > Number(currentYear)
        ? ""
        : value < 0
          ? `(${formatNumberWithDecimal(-value, 2, 2)}%)`
          : `${formatNumberWithDecimal(value, 2, 2)}%`}
    </span>
  );

  const renderFooter = (category: any, data: IEquitySummary) =>
    transformedFooter && (
      <Row>
        <Column footer={category == "Forecast" ? "Totals/averages" : ""} />
        <Column field="type" footer={category} />
        <Column footer={currencyTemplate(data?.purchasePrice)} footerStyle={{ textAlign: "right" }} />
        <Column footer={null} footerStyle={{ textAlign: "right" }} />
        <Column footer={currencyTemplate(data?.currentMarketEstimation)} footerStyle={{ textAlign: "right" }} />
        <Column footer={null} footerStyle={{ textAlign: "right" }} />
        <Column footer={currencyTemplate(data?.loanValue)} footerStyle={{ textAlign: "right" }} />
        <Column footer={percentageTemplate(data?.capitalGrowth)} footerStyle={{ textAlign: "right" }} />
        <Column footer={currencyTemplate(data?.netEquity)} footerStyle={{ textAlign: "right" }} />
      </Row>
    );

  const footerGroup = transformedFooter && (
    <ColumnGroup>
      {renderFooter("Forecast", transformedFooter.find((x: IEquitySummary) => x.type == "Forecast")!)}
      {!isForecastOnly && Number(year) <= Number(currentYear) ? (
        renderFooter("Actuals", transformedFooter.find((x: IEquitySummary) => x.type == "Actuals")!)
      ) : (
        <Row></Row>
      )}
      {!isForecastOnly && Number(year) <= Number(currentYear) ? (
        renderFooter("Variance", transformedFooter.find((x: IEquitySummary) => x.type == "Variance")!)
      ) : (
        <Row></Row>
      )}
    </ColumnGroup>
  );

  const toggleRowCollapse = (street: string) => {
    setCollapsedRows((prev) => ({ ...prev, [street]: !prev[street] }));
  };

  const renderCell = (rowData: any, field: string, templateFn: (value: number) => JSX.Element) => {
    const shouldRender = isForecastOnly
      ? collapsedRows[rowData.street] || rowData.type === "Forecast"
      : !collapsedRows[rowData.street] || rowData.type === "Forecast";

    return shouldRender ? templateFn(rowData[field]) : null;
  };

  return (
    transformedData && (
      <DataTable
        value={transformedData}
        rowGroupMode="rowspan"
        groupRowsBy="street"
        size="small"
        footerColumnGroup={isFooterVisible ? footerGroup : ""}
        rowClassName={(rowData) => (rowData.groupIndex === 1 ? "groupRowGrey" : "")}
      >
        <Column
          field="street"
          header="Address/property name"
          body={(rowData) => (
            <div className="flex items-center">
              {isForecastOnly && isFooterVisible && (
                <REIButton
                  id={`meqd-btn-collapse-${rowData.propertyId}`}
                  icon={collapsedRows[rowData.street] ? "pi pi-angle-down" : "pi pi-angle-right"}
                  text={true}
                  onClick={() => toggleRowCollapse(rowData.street)}
                />
              )}
              {!isForecastOnly && isFooterVisible && (
                <REIButton
                  id={`meqd-btn-collapse-${rowData.propertyId}`}
                  icon={collapsedRows[rowData.street] ? "pi pi-angle-right" : "pi pi-angle-down"}
                  text={true}
                  onClick={() => toggleRowCollapse(rowData.street)}
                />
              )}
              <span
                id={`meqd-lnk-address-${rowData.propertyId}`}
                data-testid={`meqd-lnk-address-${rowData.propertyId}`}
                onClick={() => {
                  equityData.isPropertyClickable &&
                    navigate(`/portfolio-tracker`, {
                      state: {
                        tab: "MyPropertyTab",
                        MyPropertyId: rowData.propertyId,
                      },
                    });
                }}
                className={`${equityData.isPropertyClickable ? "cursor-pointer text-blue-700" : ""}`}
              >{`${[rowData.street, rowData.suburb, rowData.state, rowData.postcode].filter((part) => part).join(", ")} (${rowData.country})`}</span>
            </div>
          )}
        />
        <Column field="type" header="Type" body={(rowData) => renderCell(rowData, "type", (value) => value)} />
        <Column
          field="purchasePrice"
          header="Purchase price"
          body={(rowData) => {
            if (rowData.type !== "Forecast") return "";
            return renderCell(rowData, "purchasePrice", currencyTemplate);
          }}
          align="right"
        />
        <Column
          field="purchaseDate"
          header="Purchase date"
          body={(rowData) => {
            if (!rowData.purchaseDate || rowData.purchaseDate == null || rowData.type !== "Forecast") return "";
            const cleanedDateStr = rowData.purchaseDate.split(" ")[0].replace(/\//g, "-");
            const [day, month, years] = cleanedDateStr.split("-");
            return renderCell(rowData, "purchaseDate", () => `${day}/${month}/${years}` as any);
          }}
          align="right"
        />
        <Column
          field="currentMarketEstimation"
          header="Current market estimate"
          body={(rowData) => renderCell(rowData, "currentMarketEstimation", currencyTemplate)}
          align="right"
        />
        <Column
          field="currentMarketEstimationDate"
          header="Estimate date"
          body={(rowData) => {
            if (rowData.type !== "Forecast") return "";
            return renderCell(rowData, "currentMarketEstimationDate", (value) => value as any);
          }}
          align="right"
        />
        <Column
          field="loanValue"
          header="Loan balance"
          body={(rowData) => renderCell(rowData, "loanValue", currencyTemplate)}
          align="right"
        />
        <Column
          field="capitalGrowth"
          header="Capital growth %"
          body={(rowData) => renderCell(rowData, "capitalGrowth", percentageTemplate)}
          align="right"
        />
        <Column
          field="netEquity"
          header="Net equity"
          body={(rowData) => renderCell(rowData, "netEquity", currencyTemplate)}
          align="right"
        />
      </DataTable>
    )
  );
};

export default EquityDetail;
